/**
 * 后台打印机 —— 按一下直接出纸,不弹浏览器的打印框。
 *
 * ★ 这一屏配的是「谁来取活」,不是「怎么连打印机」。
 *   浏览器打不开到 9100 端口的 TCP 连接(没有这个 API),
 *   手机/iPad 连内网打印机的 HTTP 接口又会被
 *   「HTTPS 页面不许发 HTTP」拦掉 —— 所以反过来:
 *   任务落到队列上,由打印机那一侧主动来取。
 */
import { useCallback, useEffect, useState } from 'react';
import { api, ApiFailure } from '../lib/api';

export interface PrinterRow {
  id: string;
  name: string;
  kind: 'AGENT' | 'CLOUDPRNT' | 'STATION';
  host: string | null;
  port: number;
  paperWidth: '58' | '80';
  isActive: boolean;
  /** 钥匙明文。老记录是 null —— 那时候只存了哈希,找不回来 */
  token: string | null;
  tokenHint: string;
  lastSeenAt: string | null;
  queued: number;
}

/** 多久没来取活就算离线 —— 中转每 2 秒问一次,一分钟没动静肯定是断了 */
const OFFLINE_MS = 60_000;

export function isOnline(p: PrinterRow): boolean {
  return p.lastSeenAt != null && Date.now() - new Date(p.lastSeenAt).getTime() < OFFLINE_MS;
}

export function PrinterSettings() {
  const [rows, setRows] = useState<PrinterRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [pairing, setPairing] = useState(false);
  /** 刚生成的 token —— 只有这一次能看到,所以单独摆出来 */
  const [freshToken, setFreshToken] = useState<{ name: string; token: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ data: PrinterRow[] }>('/printers');
      setRows(r.data);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读取失败');
    }
  }, []);

  useEffect(() => {
    void load();
    /*
     * ★ 每 10 秒刷一次。这一屏上「在线 / 还排着几张」是会变的,
     *   而人正是打开它来看「那台机器到底连上没有」——
     *   一屏不会自己变的状态,等于让他不停按 F5。
     */
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [load]);

  async function rotate(p: PrinterRow) {
    try {
      const r = await api<{ token: string }>(`/printers/${p.id}/rotate-token`, { method: 'POST' });
      setFreshToken({ name: p.name, token: r.token });
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '换钥匙失败');
    }
  }

  async function remove(p: PrinterRow) {
    try {
      await api(`/printers/${p.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '删除失败');
    }
  }

  return (
    <section aria-label="后台打印机">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 lg:text-2xl dark:text-slate-100">
            打印机
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            配好之后,列表里按「打印小票」直接出纸 —— 不弹打印框,手机和 iPad 上也一样
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★ 「配对新设备」排在前面,而且是主按钮 ——
              它是那条**收银机上一个字都不用敲**的路:
              那台机器显示六位码,人在这儿输进去就完了。
              反过来(先新增、再把三十多位钥匙弄过去)是难走的那条。
          */}
          <button
            onClick={() => setPairing(true)}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white"
          >
            配对新设备
          </button>
          <button
            onClick={() => setAdding(true)}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700
                       dark:border-slate-600 dark:text-slate-300"
          >
            手动新增
          </button>
        </div>
      </div>

      {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      {/*
        ★ token 只在这一刻看得见。
          说清楚「关掉就没了」,而不是让人事后回来找 ——
          事后找的结果是他重新生成一把,而老那把还配在墙角那台机器上。
      */}
      {freshToken && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm
                        dark:border-amber-800 dark:bg-amber-950/40">
          <div className="font-medium text-amber-900 dark:text-amber-200">
            「{freshToken.name}」的钥匙
          </div>
          <div className="text-xs text-amber-800 dark:text-amber-300">
            手动新增的要贴到那台机器上;配对来的它自己已经拿到了,这儿只是留着备查
          </div>
          <code className="mt-1 block break-all rounded bg-white px-2 py-1 font-mono text-xs
                           dark:bg-slate-900">
            {freshToken.token}
          </code>
          <div className="mt-1.5 flex gap-2">
            <button
              onClick={() => void navigator.clipboard?.writeText(freshToken.token)}
              className="rounded-lg bg-amber-600 px-2.5 py-1 text-xs font-medium text-white"
            >
              复制
            </button>
            <button
              onClick={() => setFreshToken(null)}
              className="text-xs text-amber-800 dark:text-amber-300"
            >
              知道了(下面列表里随时能再看到)
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center
                        text-sm text-slate-500 dark:border-slate-600">
          还没配打印机 —— 现在按「打印小票」走的是浏览器的打印框
        </div>
      )}

      <ul className="space-y-2">
        {rows.map((p) => (
          <li
            key={p.id}
            className="rounded-xl border border-slate-200 px-4 py-3 dark:border-slate-700"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-medium text-slate-900 dark:text-slate-100">{p.name}</span>
              <span className="text-xs text-slate-500">
                {/*
                  ★ 没填 IP 是**正常**的:USB 小票机就走那条路,
                    打印机在哪由那台电脑上的 config.json 说了算。
                    写成「没填 IP」会让人以为漏了一步,然后去补一个填不对的 IP。
                */}
                {p.kind === 'AGENT'
                  ? p.host
                    ? `电脑中转 · ${p.host}:${p.port}`
                    : '电脑中转 · 打印机接在那台电脑上'
                  : p.kind === 'STATION'
                    ? '浏览器打印台'
                    : '打印机自己联网'}
              </span>
              <span className="text-xs text-slate-400">{p.paperWidth}mm</span>
              {/*
                ★ 在线状态看的是「最近一次来取活」。
                  没有这一项的话,人配完之后完全不知道那头连上没有 ——
                  他只能按一下打印,然后走到打印机跟前看。
              */}
              {isOnline(p) ? (
                <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700
                                 dark:bg-green-900/40 dark:text-green-300">
                  在线
                </span>
              ) : (
                <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-500
                                 dark:bg-slate-700 dark:text-slate-400">
                  {p.lastSeenAt ? '离线' : '还没连上过'}
                </span>
              )}
              {p.queued > 0 && (
                <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700
                                 dark:bg-amber-900/40 dark:text-amber-300">
                  排着 {p.queued} 张
                </span>
              )}
              <span className="ml-auto flex gap-3 text-xs">
                <button
                  onClick={() => void rotate(p)}
                  className="text-brand-600 hover:underline dark:text-brand-400"
                >
                  换钥匙
                </button>
                <button
                  onClick={() => void remove(p)}
                  className="text-red-600 hover:underline dark:text-red-400"
                >
                  删除
                </button>
              </span>
            </div>
            {/*
              ★ 钥匙摆在这儿,随时能复制。
                藏起来的话,换台机器就得重新生成一把再跑一趟去改 ——
                而这一屏本来就守着 `setting:manage`,能看到它的人
                也能看到这家店所有别的设置。
            */}
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {p.token ? (
                <>
                  <code className="break-all rounded bg-slate-50 px-2 py-1 font-mono text-[11px]
                                   text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                    {p.token}
                  </code>
                  <button
                    onClick={() => void navigator.clipboard?.writeText(p.token!)}
                    className="text-xs text-brand-600 hover:underline dark:text-brand-400"
                  >
                    复制
                  </button>
                </>
              ) : (
                <span className="text-[11px] text-slate-400">
                  {p.tokenHint}… —— 这把是旧版建的,看不到内容;要用就点「换钥匙」
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>

      {pairing && (
        <PairDevice
          onClose={() => setPairing(false)}
          onPaired={async (name, token) => {
            setPairing(false);
            setFreshToken({ name, token });
            await load();
          }}
        />
      )}

      {adding && (
        <AddPrinter
          onClose={() => setAdding(false)}
          onAdded={async (name, token) => {
            setAdding(false);
            setFreshToken({ name, token });
            await load();
          }}
        />
      )}
    </section>
  );
}

/**
 * 认领一台正在申请的打印台。
 *
 * ★ 那台机器上显示六位码,人在这儿输进去 —— 反过来把三十多位的钥匙
 *   弄到收银机上才是难的那条:那种机器上不好复制粘贴,只能手抄。
 */
function PairDevice({
  onClose,
  onPaired,
}: {
  onClose: () => void;
  onPaired: (name: string, token: string) => Promise<void>;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [paper, setPaper] = useState<'58' | '80'>('80');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ name: string; token: string }>('/printers/pair', {
        method: 'POST',
        body: { code: code.trim(), name: name.trim() || undefined, paperWidth: paper },
      });
      await onPaired(r.name, r.token);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '配对失败');
      setBusy(false);
    }
  }

  const INPUT =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="配对新设备"
        className="w-full max-w-md rounded-xl bg-white p-5 dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">配对新设备</h2>
        {/*
          ★ 两条路都会显示六位码,而这一屏**不用问是哪一条** ——
            那台机器申请的时候就报了自己是什么,批准时照着建。
            让人在这儿选「打印台还是中转」等于让他猜,而猜错的后果是
            配对成功、在线、一张也打不出来。
        */}
        <p className="mt-1 text-sm text-slate-500">
          那台机器上会显示一个六位码 —— 输到下面。
        </p>
        <ul className="mt-1.5 space-y-0.5 text-xs text-slate-500">
          <li>· 用中转程序(没有窗口):双击「打印中转(看日志).bat」,窗口里就是那个码</li>
          <li>· 用浏览器打印台:在那台电脑上打开 <code>{location.origin}/station</code></li>
        </ul>

        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-300">那台机器上显示的码</span>
            <input
              aria-label="配对码"
              value={code}
              // ★ 自动转大写:码里只有大写字母和数字,而人多半小写打
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABC123"
              className={`${INPUT} font-mono text-lg tracking-widest`}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-300">叫什么(可不填)</span>
            <input
              aria-label="设备名字"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="收银机"
              className={INPUT}
            />
          </label>
          <div>
            <div className="mb-1 text-sm text-slate-600 dark:text-slate-300">纸多宽</div>
            <div className="flex gap-3">
              {(['80', '58'] as const).map((w) => (
                <label key={w} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    aria-label={`${w}mm`}
                    checked={paper === w}
                    onChange={() => setPaper(w)}
                  />
                  {w}mm
                </label>
              ))}
            </div>
          </div>
          {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-500">
            取消
          </button>
          <button
            onClick={() => void go()}
            disabled={busy || code.trim().length < 4}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white
                       disabled:opacity-40"
          >
            {busy ? '配对中…' : '批准'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddPrinter({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: (name: string, token: string) => Promise<void>;
}) {
  const [kind, setKind] = useState<'AGENT' | 'CLOUDPRNT' | 'STATION'>('STATION');
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('9100');
  const [paper, setPaper] = useState<'58' | '80'>('80');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /*
   * ★ IP 一律可以留空。
   *
   *   USB 小票机根本没有 IP —— 中转程序直接把字节塞进这台电脑的
   *   打印队列(留空就用默认打印机)。逼着填一个的话,人只会
   *   随手写个 127.0.0.1,而那个地址会让中转白等一轮超时。
   */
  const ready = name.trim() !== '';

  async function save() {
    setBusy(true);
    try {
      const r = await api<{ token: string }>('/printers', {
        method: 'POST',
        body: {
          name: name.trim(),
          kind,
          ...(kind === 'AGENT' ? { host: host.trim(), port: Number(port) || 9100 } : {}),
          paperWidth: paper,
        },
      });
      await onAdded(name.trim(), r.token);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '保存失败');
      setBusy(false);
    }
  }

  const INPUT =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="新增打印机"
        className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5
                   dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">新增打印机</h2>

        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-300">叫什么</span>
            <input
              aria-label="打印机名字"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="仓库那台"
              className={INPUT}
            />
          </label>

          <div>
            <div className="mb-1 text-sm text-slate-600 dark:text-slate-300">哪一种</div>
            {/*
              ★ 「这台电脑的浏览器」排第一,而且是默认 ——
                它是三种里唯一**什么都不用装**的:开着一个标签页就行。
                店里只有一台收银机、还是 Win7 的时候,这是唯一走得通的那条。
            */}
            <label className="flex items-start gap-2 py-1 text-sm">
              <input
                type="radio"
                aria-label="这台电脑的浏览器"
                className="mt-1"
                checked={kind === 'STATION'}
                onChange={() => setKind('STATION')}
              />
              <span>
                <span className="text-slate-800 dark:text-slate-100">这台电脑的浏览器</span>
                <span className="block text-xs text-slate-500">
                  接着打印机的那台电脑上开一个「打印台」页面,它收到就打。
                  <b>什么都不用装</b> —— 那个标签页要一直开着
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 py-1 text-sm">
              <input
                type="radio"
                aria-label="电脑中转"
                className="mt-1"
                checked={kind === 'AGENT'}
                onChange={() => setKind('AGENT')}
              />
              <span>
                <span className="text-slate-800 dark:text-slate-100">电脑中转</span>
                <span className="block text-xs text-slate-500">
                  接着打印机的那台电脑上跑一个小程序。
                  <b>USB 和网口的都能用</b>,而且没有窗口弹出来 —— 要那台电脑平时开着
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 py-1 text-sm">
              <input
                type="radio"
                aria-label="打印机自己联网"
                className="mt-1"
                checked={kind === 'CLOUDPRNT'}
                onChange={() => setKind('CLOUDPRNT')}
              />
              <span>
                <span className="text-slate-800 dark:text-slate-100">打印机自己联网</span>
                <span className="block text-xs text-slate-500">
                  打印机每隔几秒自己来问有没有活。<b>不需要电脑</b> ——
                  但要支持 Star CloudPRNT 协议的型号
                </span>
              </span>
            </label>
          </div>

          {kind === 'AGENT' && (
            <div className="flex gap-2">
              <label className="flex-1 text-sm">
                <span className="mb-1 block text-slate-600 dark:text-slate-300">
                  打印机 IP（USB 接的留空）
                </span>
                <input
                  aria-label="打印机 IP"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  placeholder="192.168.1.50"
                  className={INPUT}
                />
                {/*
                  ★ 说清楚留空会怎样。不说的话人会随手填个 127.0.0.1 ——
                    而那个地址会让中转白等一轮超时,然后报「拒绝连接」。
                */}
                <span className="mt-1 block text-[11px] text-slate-400">
                  USB 打印机没有 IP:留空,中转会直接用那台电脑的默认打印机
                </span>
              </label>
              <label className="w-24 text-sm">
                <span className="mb-1 block text-slate-600 dark:text-slate-300">端口</span>
                <input
                  aria-label="端口"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  className={INPUT}
                />
              </label>
            </div>
          )}

          <div>
            <div className="mb-1 text-sm text-slate-600 dark:text-slate-300">纸多宽</div>
            <div className="flex gap-3">
              {(['80', '58'] as const).map((w) => (
                <label key={w} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    aria-label={`${w}mm`}
                    checked={paper === w}
                    onChange={() => setPaper(w)}
                  />
                  {w}mm
                </label>
              ))}
            </div>
          </div>

          {kind === 'STATION' && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600
                          dark:bg-slate-900/60 dark:text-slate-300">
              存好之后会给你一串钥匙。在<b>接着打印机的那台电脑</b>上打开{' '}
              {/*
                ★ 不带 .html —— Cloudflare Pages 会把扩展名去掉,
                  带着写会先 308 跳一次。能用,但给仓库的人的地址
                  不该是「输进去会变成另一个」的那种。
              */}
              <code className="mx-1">{location.origin}/station</code>
              ,把钥匙贴进去 —— 那个页面开着,手机上按的打印就从这台机器出纸。
            </p>
          )}

          {kind === 'CLOUDPRNT' && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600
                          dark:bg-slate-900/60 dark:text-slate-300">
              存好之后会给你一串钥匙。把打印机设置里的 CloudPRNT 地址填成
              <code className="mx-1">{location.origin.replace(/^https?:\/\/[^.]+/, '')}</code>
              那台服务器的 <code>/api/v1/print/cloudprnt/&lt;钥匙&gt;</code>,它自己就会来取活。
            </p>
          )}

          {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-500">
            取消
          </button>
          <button
            onClick={() => void save()}
            disabled={busy || !ready}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white
                       disabled:opacity-40"
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
